import _ from 'underscore';
import React, { useMemo, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Wizard,
  LayoutContainer,
  Layout,
  StepContainer,
} from '@bingads-webui-react/wizard';
import {
  BusyBlocker,
  DefaultTemplate,
  CancelWizardDialog,
  ConfirmWizardDialog,
} from '@bingads-webui-react/wizard-ui-library';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { withSiteMap, getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { withScope } from '@bingads-webui-react/scope-service';
import { MmpFetchProvider } from '@bingads-webui-campaign-react/xandr-campaign';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { useShoppingFAC, ShoppingFACScenario } from '@bingads-webui-campaign-react/shopping-coupon-callout';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useConfirmDialogConfigWithLogger } from '@bingads-webui-campaign-react/campaign-settings-wizard-v1-utils';

import { getStepConfig, StepList, getFlow, getFirstStep, getDestinationWhenDone, getAdTypeFromCurrentCampaign, isSearchShoppingCampaign } from './steps';
import { AD_TYPES_TO_FLOWS, CREATE_SCENARIO } from './constants';
import { StepFooter } from './footer';

const StandardShoppingCampaignWizardInternal = (props) => {
  const {
    siteMap, scope, perfMarker,
    campaignType: adType,
    createScenario = CREATE_SCENARIO.CREATE_CAMPAIGN,
    currentCampaign = undefined,
    currentAdGroup = undefined,
    serverContext,
    permissions,
  } = props;

  const isConversionAssociationWarningP3Enabled = _.get(permissions, ['dynamic', 'ConversionAssociationWarningP3'], false);
  const isCampaignCreation = createScenario === CREATE_SCENARIO.CREATE_CAMPAIGN;
  const isCampaignUpdate = createScenario === CREATE_SCENARIO.UPDATE_CAMPAIGN;
  const routeToNavigateToWhenDone = getRoutePathById(siteMap, getDestinationWhenDone({ createScenario }));
  const i18n = useContext(I18nContext);

  const instrumentation = useContext(InstrumentationContext);

  const wizardType = adType || getAdTypeFromCurrentCampaign(currentCampaign);

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild(`StandardShoppingWizard-${wizardType}`)
  ), [perfMarker, wizardType]);

  const flowType = AD_TYPES_TO_FLOWS[wizardType];
  const initialStep = getFirstStep({ createScenario });

  const facCouponInfoRef = useRef({
    isFACCouponActive: false,
    facCouponValue: null,
  });

  const {
    isActive: isFACCouponActive,
    ShoppingFACCallout,
    adoptCouponFromActiveStatus,
    facCoupon,
  } = useShoppingFAC();

  const showFACCallout = useMemo(
    () => isSearchShoppingCampaign(flowType) && isCampaignCreation && _.get(serverContext, ['DynamicPermissions', 'FACForShopping'], false),
    [flowType, isCampaignCreation, serverContext]
  );

  useEffect(() => {
    facCouponInfoRef.current.isFACCouponActive = isFACCouponActive;
    facCouponInfoRef.current.facCouponValue = facCoupon && facCoupon.value;
  }, [isFACCouponActive, facCoupon]);

  const config = useMemo(() => ({
    id: 'standard-shopping-wizard',
    initialStep,
    steps: getStepConfig({ flowType, createScenario, permissions }),
    initialFlowFactors: { flow: flowType },
    initialWizardData: {
      flow: flowType,
      createScenario,
      campaign: currentCampaign,
      facInfo: facCouponInfoRef,
      adGroup: currentAdGroup,
    },
    getFlow: () => getFlow({ createScenario }),
    actions: {
      onDone: () => scope.save(routeToNavigateToWhenDone),
      onExited: () => scope.save(routeToNavigateToWhenDone),
    },
    attachStepActivities: true,
  }), [flowType, initialStep, routeToNavigateToWhenDone, scope, createScenario, currentCampaign, currentAdGroup, facCouponInfoRef, permissions]);

  useEffect(() => {
    setClarityTag({ StandardShopping: flowType });
  }, [flowType]);

  return (
    <PerformanceMarkerProvider
      name="StandardShoppingCampaignWizard"
      instrumentation={instrumentation}
      immediateDone={false}
    >
      <Scenario name={`StandardShopping-${flowType}`}>
        <Activity name="PAAIMWizardActivity">
          {showFACCallout && <ShoppingFACCallout scenarioName={ShoppingFACScenario.FACShoppingInSearchCampaignNew} />}
          <MmpFetchProvider>
            <Wizard config={config}>
              <WizardPerfMarker wizardName="StandardShoppingWizard" wizardMarker={wizardPerfMarker} >
                <LayoutContainer template={DefaultTemplate}>
                  {createScenario === CREATE_SCENARIO.CREATE_CAMPAIGN && <Layout type="StepList" component={StepList} />}
                  <Layout type="StepContainer" component={StepContainer} />
                  <Layout
                    type="Footer"
                    component={footerProps => <StepFooter {...footerProps} adoptCoupon={adoptCouponFromActiveStatus} />}
                    nextButtonText={i18n.getString(_TL_('Save & go to next step'))}
                  />
                  <Layout type="Blocker" component={BusyBlocker} loadingText="Processing" />
                  <Layout type="CancelDialog" component={CancelWizardDialog} />
                  {
                    isConversionAssociationWarningP3Enabled && isCampaignUpdate
                      && <Layout type="ConfirmDialog" component={ConfirmWizardDialog} config={useConfirmDialogConfigWithLogger} />
                  }
                </LayoutContainer>
              </WizardPerfMarker>
            </Wizard>
          </MmpFetchProvider>
        </Activity>
      </Scenario>
    </PerformanceMarkerProvider>
  );
};

StandardShoppingCampaignWizardInternal.propTypes = {
  siteMap: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  campaignType: PropTypes.string.isRequired,
  createScenario: PropTypes.string,
  currentCampaign: PropTypes.shape({}),
  serverContext: PropTypes.shape({}).isRequired,
  currentAdGroup: PropTypes.shape({}),
  permissions: PropTypes.shape({}),
};

StandardShoppingCampaignWizardInternal.defaultProps = {
  currentCampaign: undefined,
  createScenario: CREATE_SCENARIO.CREATE_CAMPAIGN,
  currentAdGroup: undefined,
  permissions: {},
};

export const StandardShoppingCampaignWizard = withPermissions(withScope(withSiteMap(StandardShoppingCampaignWizardInternal)));
